import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type JSX, type ReactNode } from "react";

import { marketingApi } from "../client.js";
import { CardRails, ErrorBanner, SectionCardHeader } from "../shared.js";
import { PLATFORM_LABELS } from "../types.js";
import type { Economics, KnowledgeHealth, Platform } from "../types.js";
import { BudgetBar, formatUsd } from "./studioShared.js";

function Chip({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box
      component="span"
      sx={{
        borderRadius: 999,
        px: 1,
        py: 0.25,
        fontSize: 12,
        fontWeight: 500,
        bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
        color: "primary.main",
      }}
    >
      {children}
    </Box>
  );
}

function SpendBreakdown({ econ }: { econ: Economics }): JSX.Element {
  const callers = Object.entries(econ.by_caller);
  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <BudgetBar budget={econ.budget} />
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {econ.calls} call{econ.calls === 1 ? "" : "s"} ·{" "}
        {econ.input_tokens.toLocaleString()} in / {econ.output_tokens.toLocaleString()} out tokens
        {econ.estimated ? " · estimated" : ""}
      </Typography>
      {callers.length > 0 && (
        <Box sx={{ display: "grid", gap: 0.25 }}>
          {callers.map(([caller, cost]) => (
            <Box
              key={caller}
              sx={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}
            >
              <Box component="span" sx={{ color: "text.secondary" }}>
                {caller}
              </Box>
              <Box component="span" sx={{ fontFamily: "ui-monospace, monospace" }}>
                {formatUsd(cost)}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function HealthView({ health }: { health: KnowledgeHealth }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          component="span"
          data-testid="kb-health-badge"
          sx={[
            { borderRadius: 999, px: 1, py: 0.25, fontSize: 12, fontWeight: 600 },
            health.reachable
              ? { bgcolor: (t) => alpha(t.palette.success.main, 0.16), color: "success.main" }
              : { bgcolor: (t) => alpha(t.palette.error.main, 0.16), color: "error.main" },
          ]}
        >
          {health.reachable ? "Knowledge base readable" : "Knowledge base unreachable"}
        </Box>
        {health.reachable && (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {health.distinct_docs} source{health.distinct_docs === 1 ? "" : "s"} ·{" "}
            {health.pdf_docs} from PDF{health.pdf_docs === 1 ? "" : "s"}
          </Typography>
        )}
      </Box>
      {health.detail && (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{health.detail}</Typography>
      )}
      {health.samples.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2, display: "grid", gap: 0.5 }}>
          {health.samples.map((s, i) => (
            <Box component="li" key={`${s.title}-${i}`} sx={{ fontSize: 13 }}>
              <Box component="span" sx={{ fontWeight: 500 }}>
                {s.url ? (
                  <a href={s.url} target="_blank" rel="noreferrer">
                    {s.title}
                  </a>
                ) : (
                  s.title
                )}
              </Box>
              {s.is_pdf && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.75,
                    borderRadius: 999,
                    px: 0.75,
                    py: 0.125,
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: (t) => alpha(t.palette.warning.main, 0.16),
                    color: "warning.main",
                  }}
                >
                  PDF
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * Knowledge — "what the agent knows / can do" plus the budget and a live
 * knowledge-base readability probe. Mirrors the sales agent's brain/economics
 * surfaces. The KB is fed from PDF docs, so the probe confirms the agent can
 * actually read them right now (and how many retrieved sources are PDF-backed).
 */
export function KnowledgePage(): JSX.Element {
  const caps = useQuery({
    queryKey: ["marketing", "capabilities"],
    queryFn: marketingApi.getCapabilities,
  });
  const econ = useQuery({
    queryKey: ["marketing", "economics"],
    queryFn: marketingApi.getEconomics,
    refetchInterval: 30_000,
  });
  const health = useQuery({
    queryKey: ["marketing", "kb-health"],
    queryFn: marketingApi.knowledgeHealth,
  });

  const [query, setQuery] = useState("");
  const [probed, setProbed] = useState<KnowledgeHealth | null>(null);
  const probe = useMutation({
    mutationFn: (q: string) => marketingApi.probeKnowledge(q),
    onSuccess: (h) => setProbed(h),
  });

  if (caps.isError) return <ErrorBanner error={caps.error} />;

  const shownHealth = probed ?? health.data ?? null;

  return (
    <Box sx={{ display: "grid", gap: 2 }} data-testid="marketing-knowledge">
      {/* The four short capability cards live in two balanced rails so a
          short card (Budget) never leaves a dead gap beside a tall one
          (What I can do). The two wide cards below stay full-width. */}
      <CardRails
        left={
          <>
            {/* Budget */}
            <Card>
              <SectionCardHeader
                title="Budget"
                subtitle="Estimated AI spend this budget day. The autonomous loop sleeps at the cap; Create New still works."
              />
              <CardContent sx={{ px: 2.5, py: 2 }}>
                {econ.data ? (
                  <SpendBreakdown econ={econ.data} />
                ) : (
                  <Skeleton variant="rounded" height={48} />
                )}
              </CardContent>
            </Card>

            {/* What I can do */}
            <Card>
              <SectionCardHeader title="What I can do" />
              <CardContent sx={{ px: 2.5, py: 2, display: "grid", gap: 1.5 }}>
                {!caps.data ? (
                  <Skeleton variant="rounded" height={120} />
                ) : (
                  <>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {caps.data.platforms.map((p) => (
                        <Chip key={p}>{PLATFORM_LABELS[p as Platform] ?? p}</Chip>
                      ))}
                    </Box>
                    <Box component="ul" sx={{ m: 0, pl: 2, display: "grid", gap: 0.5 }}>
                      {caps.data.skills.map((s) => (
                        <Box component="li" key={s.name} sx={{ fontSize: 13 }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {s.name}
                          </Box>{" "}
                          <Box component="span" sx={{ color: "text.secondary" }}>
                            — {s.description}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </>
        }
        right={
          <>
            {/* What I read */}
            <Card>
              <SectionCardHeader title="What I read" />
              <CardContent sx={{ px: 2.5, py: 2 }}>
                {!caps.data ? (
                  <Skeleton variant="rounded" height={96} />
                ) : (
                  <Box component="dl" sx={{ display: "grid", gap: 1, m: 0 }}>
                    {caps.data.data_access.map((d) => (
                      <Box key={d.source}>
                        <Box component="dt" sx={{ fontSize: 13, fontWeight: 600 }}>
                          {d.source}
                        </Box>
                        <Box
                          component="dd"
                          sx={{ m: 0, fontSize: 13, color: "text.secondary" }}
                        >
                          {d.scope}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* What I never do */}
            <Card>
              <SectionCardHeader title="What I never do" />
              <CardContent sx={{ px: 2.5, py: 2 }}>
                {!caps.data ? (
                  <Skeleton variant="rounded" height={96} />
                ) : (
                  <Box component="ul" sx={{ m: 0, pl: 2, display: "grid", gap: 0.5 }}>
                    {caps.data.cannot.map((c, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{ fontSize: 13, color: "text.secondary" }}
                      >
                        {c}
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </>
        }
      />

      {/* What I know */}
      <Card>
        <SectionCardHeader
          title="What I know"
          subtitle="The knowledge base I'm grounded in, plus the brand voice and audience I write for."
        />
        <CardContent sx={{ px: 2.5, py: 2, display: "grid", gap: 1.5 }}>
          {!caps.data ? (
            <Skeleton variant="rounded" height={120} />
          ) : (
            <>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                    {caps.data.knowledge.sources}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    knowledge sources
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                    {caps.data.knowledge.pdf_sources}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    from PDF docs
                  </Typography>
                </Box>
              </Box>
              {caps.data.knowledge.top_sources.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {caps.data.knowledge.top_sources.map((n) => (
                    <Chip key={n}>{n}</Chip>
                  ))}
                </Box>
              )}
              <Box
                sx={{
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  bgcolor: "surfaceSoft",
                  p: 1.5,
                  display: "grid",
                  gap: 0.75,
                }}
              >
                <Typography sx={{ fontSize: 13 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Voice:
                  </Box>{" "}
                  {caps.data.brand_voice}
                </Typography>
                <Typography sx={{ fontSize: 13 }}>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Audience:
                  </Box>{" "}
                  {caps.data.target_persona}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* KB readability probe */}
      <Card>
        <SectionCardHeader
          title="Can I read the knowledge base?"
          subtitle="A live retrieval probe. The KB is fed from PDF docs — this confirms the agent can read them right now."
        />
        <CardContent sx={{ px: 2.5, py: 2, display: "grid", gap: 1.5 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <TextField
              size="small"
              label="Probe a topic"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g. emergency mass notification"
              sx={{ minWidth: 260, flex: 1 }}
            />
            <Button
              type="button"
              variant="outlined"
              data-testid="kb-probe"
              disabled={probe.isPending || query.trim().length === 0}
              onClick={() => probe.mutate(query.trim())}
            >
              {probe.isPending ? "Checking…" : "Probe"}
            </Button>
          </Box>
          {!shownHealth ? (
            <Skeleton variant="rounded" height={64} />
          ) : (
            <HealthView health={shownHealth} />
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
